import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, ArrowUp } from "lucide-react";
import NewsletterSignup from "./NewsletterSignup";
import { SITE } from "../data/mock";

const FOOTER_NAV = [
  {
    title: "About",
    links: [
      { label: "Our Mission", href: "/about" },
      { label: "Leadership & Staff", href: "/leadership" },
      { label: "Community Impact", href: "/community-impact" },
      { label: "Blog", href: "/blog" },
    ],
  },
  {
    title: "Programs",
    links: [
      { label: "All Programs", href: "/programs" },
      { label: "Recovery Support Services", href: "/programs/rss" },
      { label: "Mobile RSS", href: "/programs/mobile-rss" },
      { label: "Justice Involved RSS", href: "/programs/justice-rss" },
    ],
  },
  {
    title: "Get Involved",
    links: [
      { label: "Need Help?", href: "/need-help" },
      { label: "Ways to Help", href: "/ways-to-help" },
      { label: "Donate", href: "/donate" },
      { label: "Contact", href: "/contact" },
    ],
  },
];

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(var(--rc-deep))] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Link to="/" className="flex items-center gap-3">
              <img src={SITE.logoWhite} alt="Recovery Connections logo" className="w-12 h-12 object-contain" />
              <div>
                <div className="font-serif-display text-[20px] font-semibold">
                  Recovery Connections
                </div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-white/60">
                  of Central Florida
                </div>
              </div>
            </Link>
            <p className="mt-6 text-[14.5px] leading-[1.75] text-white/75 max-w-md">
              A 501(c)(3) nonprofit Recovery Community Organization led by Certified Recovery Peer Specialists — providing compassionate, peer-led support across Central Florida.
            </p>

            <div className="mt-7">
              <div className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold mb-3">
                Newsletter
              </div>
              <div className="max-w-md">
                <NewsletterSignup variant="dark" />
              </div>
            </div>

            <div className="mt-7 flex items-center gap-3">
              {[Facebook, Instagram, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Social link"
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:border-[hsl(var(--rc-gold))] hover:bg-white/5 transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Nav columns */}
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-8">
            {FOOTER_NAV.map((col) => (
              <div key={col.title}>
                <div className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
                  {col.title}
                </div>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.href}
                        className="text-[14px] text-white/80 hover:text-white transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Helpline + notes */}
        <div className="py-10 border-b border-white/10 grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <div className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
              Helpline
            </div>
            <a
              href={`tel:${SITE.helplineTel}`}
              className="mt-3 block font-serif-display text-[26px] font-semibold hover:text-[hsl(var(--rc-gold))] transition-colors"
            >
              {SITE.helpline}
            </a>
            <div className="text-[14px] text-white/70">{SITE.helplineDigits}</div>
          </div>
          <div className="space-y-4">
            <p className="text-[12.5px] leading-[1.7] text-white/75">
              For Peer Recovery Support Services, auxiliary aid and interpreter services for hearing-disabled and limited-English proficient persons are available at no cost to participants and their companions.
            </p>
            <p className="text-[12px] leading-[1.7] text-white/65">
              All employees and contractors are required to complete background screening through the approved provider via the Florida Clearinghouse.
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <p className="text-[12.5px] text-white/65">
            © {year} Recovery Connections of Central Florida. All rights reserved.
          </p>
          <p className="text-[12.5px] text-white/75">
            Website Designed By{" "}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-white hover:text-[hsl(var(--rc-gold))] transition-colors underline-offset-4 hover:underline"
            >
              Brand Partners
            </a>
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-[12.5px] text-white/75 hover:text-white transition-colors"
            aria-label="Back to top"
          >
            <ArrowUp className="w-3.5 h-3.5" />
            Back to top
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
